
import { Component, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import { GlobalBusNames, SearchItem, StoreActions } from '@/templates/DataNames';
import { convertToHtmlHelper, currentInstance, parseLanguageHelper } from '@/heplers';
import { Action, Getter, State } from 'vuex-class';
import SearchInput from './SearchInput.vue';
import GlobalBus from '@/plugins/GlobalBus';
import DocButton from '@/components/ui/buttons/DocButton.vue';
import DocIcon from '@/components/ui/icons/DocIcon.vue';
import { RootNamesInterface, treatmentSearchData } from '@/heplers/searchHelpers';
import Breadcrumbs from '@/components/ui/Breadcrumbs.vue';
import { parseAdvancedHrefHelper } from '@/heplers/stringHelpers';

interface RouteQueries {
  q: string;
  page: string;
  'cats[]': string[];
}

interface PaginationFromApi {
  page: number;
  pageCount: number;
  pageSize: number;
  totalCount: number;
}

interface ViewedPagination {
  title: string;
  is_link: boolean;
}

@Component({
  components: {
    Breadcrumbs,
    SearchInput,
    DocIcon,
    DocButton,
  },
  metaInfo() {
    return {
      title: 'Search',
    };
  },
  filters: {
    parseLanguage: parseLanguageHelper,
  },
})

/**
 * Из-за того что нельзя дублировать информацию в всплывающем поиске и в поиске на странице, в странице поиска делаем запрос через actions с рендером результата после пуша, а в всплывахе делем поиск после каждого события input напрямую
 */
export default class SearchPage extends Vue {
  @Action(StoreActions.SEARCH) actionSearch: (search: string) => Promise<() => void>;
  @Getter('getSearchInfo') searchResult: any;
  @State rootNames: RootNamesInterface;
  @State isMobile: boolean;
  public parseData: { [id: string]: SearchItem[] } = {};
  public inputString = '';
  public countResults = 0;
  public paginationArray: ViewedPagination[] = [];
  public pagination: PaginationFromApi = {
    page: 1,
    pageCount: 1,
    pageSize: 10,
    totalCount: 0,
  };
  /**
   * переменная для инпута ввода страницы на которую хотим перейти. Отличается от серверной на (i+1)
   */
  private selectedPage = '1';
  /**
   * номер страницы, которую выбирает в инпуте пользователь. Отличается от номера страницы которую посылаем в запросе на (i + 1)
   */
  private currentPage = '1';
  private selectedFilters: string[] = [];
  private selectedCategories: string[] = [];

  get isFirstPage() {
    return this.currentPage === '1';
  }

  get inputStringInHeader() {
    return this.inputString.length ? `"${this.inputString}"` : '';
  }

  get isKit() {
    return currentInstance === 'kit';
  }

  get searchPageClasses() {
    return {
      'is-void': !this.showSearchData && !this.inputString.length,
      'is-empty': !this.showSearchData && this.inputString.length,
    };
  }

  get showSearchData() {
    return Object.keys(this.parseData).length;
  }

  get isLastPage() {
    return +this.currentPage === this.pagination.pageCount;
  }

  parseHref(fqdn: string, kind: string) {
    return parseAdvancedHrefHelper(fqdn, kind);
  }

  beforeMount() {
    GlobalBus.off(GlobalBusNames.RENDERSEARCH);
  }

  // Ждем пока вычислится rootNames чтобы проставить выбранный фильтр
  @Watch('rootNames')
  onRootNamesRendered(_values: any) {
    //@ts-expect-error: expect error
    this.selectedFilters = this.selectedCategories.map((cat: string) => this.rootNames[cat]);
  }

  @Watch('isMobile')
  onChangeMobile(isShowMobile: boolean) {
    this.treatmentPagination(isShowMobile);
  }

  mounted() {
    GlobalBus.on(GlobalBusNames.RENDERSEARCH, this.renderSearch);
    this.renderSearch();
    this.listenKeyKombination();
  }

  listenKeyKombination() {
    document.addEventListener('keydown', (event: any) => {
      if (this.pagination.pageCount <= 1) return;

      if (event.ctrlKey && event.key === 'ArrowRight') this.goToSelectedPage(+this.currentPage + 1);

      if (event.ctrlKey && event.key === 'ArrowLeft') this.goToSelectedPage(+this.currentPage - 1);
    });
  }

  /**
   *  Основной метод для перехода по выбранным фильтрам. Чекаем не там ли мы уже, смотрим что пришло, обрезаем пустые поля, пушим роут
   *
   */
  goToSelectedPage(index?: string | number) {
    if (index === this.currentPage) return;

    let url = currentInstance === 'vox' ? '/docs/search' : '/kit/docs/search';

    let sortedBy: any = this.$router.currentRoute?.query['sort']?.length
      ? this.$router.currentRoute?.query['sort']
      : null;

    // Если есть категории то предварительно убиваем их, ибо роутер не понимает что мы изменили что-либо и показывает duplicate route
    if (this.$router.currentRoute?.query['cats[]']?.length)
      this.$router.currentRoute.query['cats[]'] = null;

    const params = {
      path: url,
      query: {
        q: this.inputString,
        ...(index ? { page: this.preparedPageIndex(index) } : null),
        ...(this.selectedCategories.length ? { 'cats[]': this.selectedCategories } : null),
        ...(sortedBy ? { sort: sortedBy } : null),
      },
    };

    this.$router.push(params).catch((error) => error);
  }

  toggleFilter(key: keyof RootNamesInterface) {
    let value = this.rootNames[key];

    if (this.selectedFilters.includes(value)) {
      let indexValue = this.selectedFilters.indexOf(value);
      let indexKey = this.selectedCategories.indexOf(key);

      this.selectedFilters.splice(indexValue, 1);
      this.selectedCategories.splice(indexKey, 1);
    } else {
      this.selectedFilters.push(value);
      this.selectedCategories.push(key);
    }
  }

  pushToSelectedPage() {
    this.goToSelectedPage(this.selectedPage);
  }

  computePaginationLinkClasses(index: string) {
    return {
      'is-current': index === this.currentPage,
      'is-last': +index === this.pagination.pageCount,
      'is-first': +index === 1,
      'is-medium': +index >= 100,
      'is-big': +index >= 1000,
    };
  }

  convertToHtml(mdString: string) {
    mdString = parseLanguageHelper(mdString);

    return convertToHtmlHelper(mdString);
  }

  /**
   * Проксируем метод чтобы в переменную index не передалось координаты поля
   */
  applyFilter() {
    this.goToSelectedPage();
  }

  clearFilter() {
    this.selectedCategories = [];
    this.selectedFilters = [];
    this.goToSelectedPage();
  }

  /**
   *  Делаем запрос по тому что у нас в поисковой строке, подготавливаем данные для вывода
   *
   */
  private renderSearch() {
    this.prepareParameters();
    if (!window.location.search) return;

    let pageNumber: any = window.location.search.match(/page=(\d*)/) || '';
    if (pageNumber.length > 1) pageNumber = +pageNumber[1] > 0 ? +pageNumber[1] - 1 : 0;

    let preparedSearchString = window.location.search.replace(
      /page=(\d*)/,
      typeof pageNumber === 'number' ? 'page=' + pageNumber : ''
    );

    this.actionSearch(preparedSearchString).then(() => {
      this.parseData = treatmentSearchData(
        this.searchResult.results,
        this.inputString,
        this.rootNames,
        this.$store.state.tree
      );

      this.pagination = this.searchResult.pagination;
      this.countResults = this.pagination.totalCount;

      this.treatmentPagination(document.body.clientWidth < 1024);
    });
  }

  /**
   *  Подготавливаем параметры поиска, вытаскивая исходные параметры из роутера
   *
   */
  private prepareParameters() {
    //@ts-expect-error: Type 'Dictionary<string | string[]>' is missing the following properties from type 'RouteQueries'
    const currentRoute: RouteQueries = this.$router.currentRoute.query;

    this.currentPage = currentRoute.page ? currentRoute.page : '1';

    let parseCategories: string[] = [];

    if (Array.isArray(currentRoute['cats[]'])) parseCategories = currentRoute['cats[]'];
    else if (currentRoute['cats[]']) parseCategories = [currentRoute['cats[]']];

    this.selectedCategories = parseCategories;

    this.inputString = currentRoute.q || '';
    //@ts-expect-error: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'RootNamesInterface'
    this.selectedFilters = this.selectedCategories.map((cat: string) => this.rootNames[cat]);
  }

  /**
   *  Составляем пагинацию. В начале всегда показываем 3 страницы, в конце 3, слева и справа от текущей страницы по 2 элемента. Остальные скрываем справа и слева троеточиями.
   *
   * @return ViewedPagination[]
   */
  private treatmentPagination(isMobile: boolean) {
    const paginationCount = this.pagination.pageCount;
    const [countsLinkFromEdges, countLinksAroundCurrentPage] = isMobile ? [1, 0] : [3, 2];
    let tempNewPagination: ViewedPagination[] = [];
    let [plusDiffFromCurrent, minusDiffFromCurrent] = [0, 0];

    for (let i = 1; i <= paginationCount; i++) {
      let item = {
        title: i.toString(),
        is_link: false,
      };

      let differenceFromCurrentPage = +this.currentPage - i;

      if (i <= countsLinkFromEdges) item.is_link = true;

      if (paginationCount - i < countsLinkFromEdges) item.is_link = true;

      if (Math.abs(differenceFromCurrentPage) <= countLinksAroundCurrentPage) item.is_link = true;
      else {
        // Если у нас еще нет троеточия слева или справа, то пушим в массив элемент троеточия, иначе оставляем как есть. Возвращаем либо новый, либо исходный массив
        if (!item.is_link)
          if (differenceFromCurrentPage > 0) {
            plusDiffFromCurrent++;
            if (plusDiffFromCurrent <= 1) {
              item.title = '...';
              tempNewPagination.push(item);
            }
          } else {
            minusDiffFromCurrent++;
            if (minusDiffFromCurrent <= 1) {
              item.title = '...';
              tempNewPagination.push(item);
            }
          }
      }

      if (item.is_link) tempNewPagination.push(item);
    }

    this.paginationArray = tempNewPagination;
  }

  private preparedPageIndex(page: string | number): string {
    return page.toString();
  }
}
